import './EditView.scss';
import { Header } from '../../Header/Header';
import { autobind, createRef } from 'office-ui-fabric-react/lib/Utilities';
import {
  PrimaryButton,
} from 'office-ui-fabric-react/lib/Button';
import { ISharingInformation, ISharingStore, SharingLinkKind, ISharingLinkSettings } from '../../../interfaces/SharingInterfaces';
import { ShareViewState } from '../../Share/Share';
import * as React from 'react';
import stringsResx from '../../Share.resx';
import PeoplePicker from '../../PeoplePicker/PeoplePicker';
import { RoleSelector } from '../../RoleSelector/RoleSelector';
import * as PeoplePickerHelper from '../../../utilities/PeoplePickerHelper';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { SharingRole } from '../../../interfaces/SharingInterfaces';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import * as PropTypes from 'prop-types';

const UNVALIDATED_EMAIL_ADDRESS = 'UNVALIDATED_EMAIL_ADDRESS'; // Person PrincipalType value to ignore when adding to cache.

export interface IEditViewRequestType {
  notifiedRecipients: boolean;
  requestedAccess: boolean;
}

export interface IEditViewProps {
  sharingInformation: ISharingInformation;
  clientId: number;
  onSuccess?: (IEditViewResult) => void;
  onFailure?: (IEditViewResult) => void;
  onLoading?: (IEditViewResult) => void;
  currentSettings: ISharingLinkSettings;
  onViewPolicyTipClicked: () => void;
}

export interface IEditViewState {
  people: IPerson[];
  messageText: string;
  roleSelection: SharingRole;
  notifyRecipients: boolean;
  errorMessage: JSX.Element;
}

export class EditView extends React.Component<IEditViewProps, IEditViewState> {
  private _store: ISharingStore;
  private _peoplePickerWrapper = createRef<HTMLElement>();

  static contextTypes = {
    sharingStore: PropTypes.object.isRequired
  };

  constructor(props: IEditViewProps, context: any) {
    super(props);

    this._store = context.sharingStore;
    this._store.addListener(() => {
      const addPeopleResult = this._store.getAddPeopleResult();
      if (addPeopleResult !== null) {
        if (addPeopleResult && this.props.onSuccess) {
          this.props.onSuccess({ notifiedRecipients: this.state.notifyRecipients, requestedAccess: this.userRequiresAccessApproval() });
        } else if (this.props.onFailure) {
          this.props.onFailure({ notifiedRecipients: this.state.notifyRecipients, requestedAccess: this.userRequiresAccessApproval() });
        }
      }
    });

    // Default sharing role is the same as the user's current role, owner is not a selection though so that gets maped to edit
    let defaultRole: SharingRole;
    if (this.props.sharingInformation.userRole === SharingRole.owner) {
      defaultRole = SharingRole.edit;
    } else {
      defaultRole = this.props.sharingInformation.userRole;
    }

    const peoplePickerError = PeoplePickerHelper.renderPickerError({
      selectedItems: this.props.currentSettings.specificPeople,
      sharingLinkKind: SharingLinkKind.direct,
      canAddExternalPrincipal: this.props.sharingInformation.canAddExternalPrincipal,
      sharingInformation: this.props.sharingInformation,
      viewPolicyTipCallback: props.onViewPolicyTipClicked,
    });

    this.state = {
      people: [],
      messageText: '',
      roleSelection: defaultRole,
      notifyRecipients: true,
      errorMessage: peoplePickerError,
    };
  }

  public render(): React.ReactElement<{}> {
    return (
      <div className={ 'od-AddPeople-Edit' }>
        <div className='od-AddPeople-Edit-Header'>
          { this._renderHeader() }
        </div>
        <div className='od-AddPeople-Edit-Section'>
          { this._renderPeoplePicker() }
          { this._renderRoleSelector() }
          { this._renderMessageArea() }
          { this._renderNotificationSelector() }
          { this._renderAddButton() }
        </div>
      </div>
    );
  }

  private isAddButtonEnabled(): boolean {
    return this.state.people.length > 0 && this.state.errorMessage === null;
  }

  private userRequiresAccessApproval(): boolean {
    return this.props.sharingInformation.requiresAccessApproval;
  }

  // This apparently requires digging into the raw person data, person.isExternal includes ALL external users
  private isUnverifiedExternal(person: IPerson): boolean {
    /* tslint:disable:no-string-literal */
    return person.rawPersonData && person.rawPersonData['EntityData'] && person.rawPersonData['EntityData']['PrincipalType'] === UNVALIDATED_EMAIL_ADDRESS;
    /* tslint:enable:no-string-literal */
  }

  private numberSelectedUnverifiedExternals(): number {
    return this.state.people.filter(person => this.isUnverifiedExternal(person)).length;
  }

  @autobind
  private _renderPeoplePicker() {

    const selectedItems = this.state.people;
    const oversharingExternalsWarning = PeoplePickerHelper.getOversharingExternalsWarning(selectedItems);
    const oversharingGroupsWarning = PeoplePickerHelper.getOversharingGroupsWarning(selectedItems, 0);

    return (
      <div className='od-Share-PeoplePicker' ref={ this._peoplePickerWrapper }>
        <PeoplePicker
          defaultSelectedItems={ this.state.people }
          error={ this.state.errorMessage }
          onChange={ this._onPeoplePickerChange }
          oversharingExternalsWarning={ oversharingExternalsWarning }
          oversharingGroupsWarning={ oversharingGroupsWarning }
          pickerSettings={ this.props.sharingInformation.peoplePickerSettings }
          sharingLinkKind={ SharingLinkKind.direct }
          sharingInformation={ this.props.sharingInformation }
          clientId={ this.props.clientId }
          allowSPGroups={ true }
          disableGraph={ true }
          onResolvedSuggestions={ null }
        />
      </div>
    );
  }

  @autobind
  private _renderHeader() {
    return (
      <Header
        clientId={ this.props.clientId }
        item={ this.props.sharingInformation.item }
        viewState={ this.userRequiresAccessApproval() ? ShareViewState.addPeopleRequestAccess : ShareViewState.addPeople }
      />
    );
  }

  @autobind
  private _renderMessageArea(): JSX.Element {

    // Message area is only rendered if the notification checkbox is selected
    if (this.state.notifyRecipients) {
      return (
        <TextField
          className='od-AddPeople-Message'
          disabled={ !this.state.notifyRecipients }
          placeholder={ stringsResx.messagePlaceholder }
          value={ this.state.messageText }
          ariaLabel={ stringsResx.messageAriaLabel }
          multiline resizable={ false }
          onChanged={ this._onMessageChange }
        />
      );
    }

  }

  private _renderRoleSelector(): JSX.Element {
    return (
      <div className='od-AddPeople-RoleSelector'>
        <RoleSelector
          onRoleChange={ this._onRoleSelectorChange }
          role={ this.state.roleSelection }
          clientId={ this.props.clientId }
          appmap={ this.props.sharingInformation.item.appmap }
          ariaLabel={ stringsResx.addPeopleRoleSelectorAriaLabel }
        />
      </div>
    );
  }

  private _renderNotificationSelector(): JSX.Element {

    // Notification selector must be disabled if the user has selected any externals
    const disabled: boolean = this.numberSelectedUnverifiedExternals() > 0;

    return (
      <Checkbox
        className='od-AddPeople-NotificationSelector'
        disabled={ disabled }
        label={ stringsResx.notifyPeople }
        onChange={ this._onAllowEditChange }
        checked={ this.state.notifyRecipients }
      />
    );
  }

  private _renderAddButton(): JSX.Element {
    const addButtonText = this.userRequiresAccessApproval() ? stringsResx.requestAccessButtonLabel : stringsResx.addButtonLabel;

    return (
      <PrimaryButton disabled={ !this.isAddButtonEnabled() } onClick={ this._onAddButtonClick } className='od-AddPeople-AddButton'>
        { addButtonText }
      </PrimaryButton>
    );
  }

  @autobind
  private _onPeoplePickerChange(items?: IPerson[]): void {
    this.setState((previousState: IEditViewState, props: IEditViewProps) => {
      // Get people picker error based on resolved items in the picker.
      const peoplePickerError = PeoplePickerHelper.renderPickerError({
        selectedItems: items,
        sharingLinkKind: SharingLinkKind.direct,
        canAddExternalPrincipal: props.sharingInformation.canAddExternalPrincipal,
        sharingInformation: props.sharingInformation,
        viewPolicyTipCallback: () => null,
      });

      // If the user has selected any externals, then notifications are required
      const notify: boolean = this.state.notifyRecipients || items.filter(person => person.isExternal).length > 0;

      return {
        ...previousState,
        people: items,
        notifyRecipients: notify,
        errorMessage: peoplePickerError,
      };
    });
  }

  @autobind
  private _onMessageChange(newValue: any): void {
    this.setState({
      messageText: newValue
    });
  }

  @autobind
  private _onRoleSelectorChange(role: SharingRole): void {
    this.setState({
      roleSelection: role
    });
  }

  @autobind
  private _onAllowEditChange(evt: React.SyntheticEvent<{}>, value: boolean): void {
    this.setState({
      notifyRecipients: value
    });
  }

  @autobind
  private _onAddButtonClick(): void {

    const peoplePickerInput = this._peoplePickerWrapper.current.querySelector('.od-Share-PeoplePicker .ms-BasePicker-input') as HTMLInputElement;
    if (peoplePickerInput && peoplePickerInput.value) {
      this.setState({
        ...this.state,
        errorMessage: <span>{ stringsResx.unresolvedTextError }</span>
      });
      return;
    }

    // Make the request to the server
    let message: string = this.state.messageText || null;
    this._store.addPeople(this.state.people, this.state.roleSelection, this.userRequiresAccessApproval(), this.state.notifyRecipients, message);

    // Notify parent that we are waiting for a response
    if (this.props.onLoading) {
      this.props.onLoading({ notifiedRecipients: this.state.notifyRecipients, requestedAccess: this.userRequiresAccessApproval() });
    }
  }
}
